import { ReactNode, useEffect, useRef, useState } from 'react';

interface Props {
  children: ReactNode;
  // How far outside the viewport the placeholder may be when rendering
  // starts, so the content is usually ready before it scrolls into view.
  // Kept small: on a phone the home page's sections start about one screen
  // below the top, and a wider margin rendered them during start-up anyway.
  rootMargin?: string;
}

// Renders its children only once the visitor scrolls near them. Until then it
// renders an empty marker, which is what the home page's data-driven sections
// rendered anyway until their data arrived, so the server HTML is unchanged.
// Keeps their data fetching, carousel measuring and rendering out of the
// page's start-up, where PageSpeed counts it as blocking time.
const RenderWhenNear: React.FC<Props> = ({
  children,
  rootMargin = '200px 0px',
}) => {
  const markerRef = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    if (near) return;
    const marker = markerRef.current;
    if (!marker || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) setNear(true);
      },
      { rootMargin },
    );
    observer.observe(marker);
    return () => observer.disconnect();
  }, [near, rootMargin]);

  if (near) return <>{children}</>;
  return <div ref={markerRef} aria-hidden />;
};

export default RenderWhenNear;
